import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/16/solid';
import { AnimatePresence, motion } from 'motion/react';
import { Fragment, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import OptionalLink from '@/components/shared/OptionalLink';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Skeleton } from '@/components/ui/daisy';
import { expoOut, fadeIn } from '@/utils/animations/commom';

type LessonNeighborsNavProps = {
  previousHref?: string;
  nextHref?: string;
  isLoading?: boolean;
  className?: string;
};

export default function LessonNeighborsNav({
  previousHref,
  nextHref,
  isLoading,
  className,
}: LessonNeighborsNavProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLessons.lessonPage.navigation',
  });

  if (!isLoading && !previousHref && !nextHref) return null;

  return (
    <nav
      aria-label={t('label')}
      className={twMerge('hidden items-center gap-4 lg:flex', className)}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={isLoading ? 'skeleton' : 'links'}
          {...fadeIn}
          transition={expoOut}
          className="flex items-center gap-4"
        >
          <ConditionalRenderer
            condition={!isLoading}
            fallback={<NeighborsSkeleton />}
          >
            <NeighborChip href={previousHref}>
              <ChevronLeftIcon className="size-5" />
              {t('previous')}
            </NeighborChip>
            <NeighborChip href={nextHref}>
              {t('next')}
              <ChevronRightIcon className="size-5" />
            </NeighborChip>
          </ConditionalRenderer>
        </motion.div>
      </AnimatePresence>
    </nav>
  );
}

type NeighborChipProps = {
  href?: string;
  children: ReactNode;
};

function NeighborChip({ href, children }: NeighborChipProps) {
  const className = twMerge(
    'btn btn-sm gap-1 rounded-2xl border-none px-2 h-9 text-xs font-normal shadow-none last:pl-4 first:pr-4',
    href
      ? 'btn-soft bg-accent/10 text-base-content hover:bg-accent/20'
      : 'btn-disabled bg-base-200 text-base-content/40',
  );

  return (
    <OptionalLink href={href} className={className}>
      {children}
    </OptionalLink>
  );
}

function NeighborsSkeleton() {
  return (
    <Fragment>
      <Skeleton className="h-8 w-32 rounded-2xl" />
      <Skeleton className="h-8 w-32 rounded-2xl" />
    </Fragment>
  );
}
